import posthog from "posthog-js";

const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY;
const POSTHOG_HOST =
  process.env.REACT_APP_POSTHOG_HOST || "https://us.i.posthog.com";

let initialized = false;

// PostHog is a non-essential analytics cookie, which ePrivacy requires consent
// for BEFORE it is set, so nothing loads until the visitor has chosen. Stored
// per-browser; clearing site data re-prompts.
const CONSENT_KEY = "jh_cookie_consent";
// The choice lives in a cookie, not localStorage: signing out calls
// localStorage.clear(), which wiped it and re-asked every returning user. Six
// months is the usual re-ask interval for analytics consent.
const CONSENT_MAX_AGE_SECONDS = 60 * 60 * 24 * 180;

function readConsentCookie() {
  const match = document.cookie
    .split(";")
    .map((c) => c.trim())
    .find((c) => c.startsWith(`${CONSENT_KEY}=`));
  return match ? match.slice(CONSENT_KEY.length + 1) : null;
}

function writeConsentCookie(value) {
  // Host-only (no domain attribute): www redirects to the apex, so there is no
  // second origin to share with, and an explicit domain would break localhost.
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie =
    `${CONSENT_KEY}=${value}; Max-Age=${CONSENT_MAX_AGE_SECONDS}; Path=/; SameSite=Lax${secure}`;
  // Drop the old localStorage mirror once the cookie holds the answer.
  // Otherwise clearing cookies later would resurrect a stale "granted" from a
  // visitor who has since declined.
  try {
    localStorage.removeItem(CONSENT_KEY);
  } catch (error) {
    /* storage unavailable */
  }
}

export function getAnalyticsConsent() {
  const fromCookie = readConsentCookie();
  if (fromCookie) return fromCookie;
  // Carry over a choice made before consent moved off localStorage, so anyone
  // who already answered is not asked a second time.
  try {
    const legacy = localStorage.getItem(CONSENT_KEY);
    if (legacy) {
      writeConsentCookie(legacy);
      return legacy;
    }
  } catch (error) {
    /* storage unavailable */
  }
  return null;
}

// Declining has to remove identifiers PostHog already stored, not just stop new
// ones: a visitor who declines while holding a ph_* cookie from an earlier visit
// would otherwise stay trackable by the very id they just refused.
function clearAnalyticsStorage() {
  const isPostHogKey = (k) => /^ph_|posthog/i.test(k);
  try {
    Object.keys(localStorage)
      .filter(isPostHogKey)
      .forEach((k) => localStorage.removeItem(k));
  } catch (error) {
    /* ignore: storage unavailable */
  }
  document.cookie
    .split(";")
    .map((c) => c.split("=")[0].trim())
    .filter(isPostHogKey)
    .forEach((name) => {
      // Clear on this host and on the parent domain, since PostHog sets cookies
      // on the registrable domain and the two are separate entries.
      const parent = window.location.hostname.split(".").slice(-2).join(".");
      [window.location.hostname, `.${parent}`].forEach((domain) => {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${domain}`;
      });
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    });
}

export function setAnalyticsConsent(granted) {
  // If cookies are blocked entirely this silently no-ops and the banner returns
  // next visit — the same behaviour private mode already had, and the safe way
  // round: we never assume consent we could not record.
  writeConsentCookie(granted ? "granted" : "denied");
  if (granted) {
    initAnalytics();
    return;
  }
  if (initialized) resetAnalytics();
  clearAnalyticsStorage();
}

// Safe no-op until REACT_APP_POSTHOG_KEY is configured, so the app runs
// unchanged in local dev and in environments without analytics.
export function initAnalytics() {
  if (getAnalyticsConsent() !== "granted") return;
  if (!POSTHOG_KEY || initialized) return;
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    // "2025-05-24" defaults include history-change pageviews, which covers
    // SPA route changes without a manual $pageview hook.
    defaults: "2025-05-24",
    // Autocapture records the text of whatever the user clicked. Our EEO
    // questions render each answer as a button labelled with the answer itself
    // ("Yes" under "Do you have a disability?"), so autocapture would ship
    // disability, LGBTQ, gender and veteran status to PostHog against a user we
    // identify by name and email. That is Article 9 special-category data, it
    // contradicts the "sensitive categories: N/A" declaration in our signed DPA
    // annex, and it contradicts our Privacy Policy promise that this data is
    // used only to answer the matching application question. Events we care
    // about are captured explicitly via capture() instead.
    autocapture: false,
    // Replay would capture the same fields as they are filled in.
    disable_session_recording: true,
  });
  initialized = true;
}

export function capture(event, properties) {
  if (initialized) posthog.capture(event, properties);
}

export function identify(userId, properties) {
  if (initialized) posthog.identify(userId, properties);
}

export function resetAnalytics() {
  if (initialized) posthog.reset();
}
